export const ORPHAN_GAP_POLICY_CONTRACT_VERSION = 1;
export const ORPHAN_GAP_DEFAULT_FAR_HORIZON_DAYS = 21;
export const ORPHAN_GAP_MAX_FAR_HORIZON_DAYS = 21;

function normalizePositiveInteger(value) {
  const numeric = Number(value);
  return Number.isSafeInteger(numeric) && numeric > 0 ? numeric : null;
}

function normalizeHorizon(value) {
  const numeric = Number(value);
  return Number.isInteger(numeric) &&
    numeric >= 1 &&
    numeric <= ORPHAN_GAP_MAX_FAR_HORIZON_DAYS
    ? numeric
    : ORPHAN_GAP_DEFAULT_FAR_HORIZON_DAYS;
}

export function normalizeOrphanGapPolicyDraft(runtime = {}, strategies = []) {
  const runtimeItems = Array.isArray(runtime?.items) ? runtime.items : [];
  const itemByStrategyId = new Map(
    runtimeItems
      .map((item) => [
        normalizePositiveInteger(item?.strategy_setting_id),
        item,
      ])
      .filter(([strategyId]) => strategyId != null)
  );

  const normalizedStrategies = (Array.isArray(strategies) ? strategies : [])
    .map((strategy) => ({
      id: normalizePositiveInteger(strategy?.id),
      scope_type: String(strategy?.scope_type || "hotel"),
      scope_id: strategy?.scope_id ?? null,
      strategy_mode: String(strategy?.strategy_mode || "balanced"),
      is_active: strategy?.is_active === true,
    }))
    .filter((strategy) => strategy.id != null)
    .sort((left, right) => left.id - right.id);

  return {
    contract_version: Number(runtime?.contract_version || 0),
    business_unit_id: normalizePositiveInteger(runtime?.business_unit_id),
    expected_configuration_hash:
      typeof runtime?.configuration_hash === "string" && runtime.configuration_hash.trim()
        ? runtime.configuration_hash.trim()
        : null,
    items: normalizedStrategies.map((strategy) => {
      const source = itemByStrategyId.get(strategy.id) || {};
      return {
        strategy_setting_id: strategy.id,
        // UI-only provenance. The backend accepts sparse policy rows and
        // resolves a category without an explicit row through the hotel
        // policy. Keep that distinction after expanding the draft to every
        // strategy; buildOrphanGapPolicyPayload deliberately does not emit it.
        policy_is_configured: itemByStrategyId.has(strategy.id),
        scope_type: strategy.scope_type,
        scope_id: strategy.scope_id,
        strategy_mode: strategy.strategy_mode,
        strategy_is_active: strategy.is_active,
        is_enabled: source?.is_enabled === true,
        gap_1_enabled: source?.gap_1_enabled === true,
        gap_2_enabled: source?.gap_2_enabled === true,
        far_horizon_days: String(normalizeHorizon(source?.far_horizon_days)),
        reassignment_required: true,
      };
    }),
  };
}

export function updateOrphanGapPolicyDraftItem(
  draft,
  strategySettingId,
  field,
  value
) {
  const strategyId = normalizePositiveInteger(strategySettingId);
  if (strategyId == null) return draft;

  return {
    ...(draft || {}),
    items: (Array.isArray(draft?.items) ? draft.items : []).map((item) => {
      if (Number(item.strategy_setting_id) !== strategyId) return item;
      if (field === "reassignment_required") return item;
      if (!["is_enabled", "gap_1_enabled", "gap_2_enabled", "far_horizon_days"].includes(field)) {
        return item;
      }
      if (field === "is_enabled" && value !== true) {
        return {
          ...item,
          is_enabled: false,
          gap_1_enabled: false,
          gap_2_enabled: false,
        };
      }
      return {
        ...item,
        [field]: field === "far_horizon_days" ? String(value) : value === true,
      };
    }),
  };
}

export function validateOrphanGapPolicyDraft(draft) {
  const errors = [];
  const items = Array.isArray(draft?.items) ? draft.items : [];
  const seen = new Set();

  items.forEach((item) => {
    const strategyId = normalizePositiveInteger(item?.strategy_setting_id);
    if (strategyId == null || seen.has(strategyId)) {
      errors.push("Each strategy must appear exactly once.");
    } else {
      seen.add(strategyId);
    }

    if (item?.reassignment_required !== true) {
      errors.push("Reassignment assessment must remain mandatory.");
    }

    const horizon = Number(item?.far_horizon_days);
    if (!Number.isInteger(horizon) || horizon < 1 || horizon > ORPHAN_GAP_MAX_FAR_HORIZON_DAYS) {
      errors.push("The far horizon must be a whole number from 1 to 21 days.");
    }

    if (item?.is_enabled === true && item?.gap_1_enabled !== true && item?.gap_2_enabled !== true) {
      errors.push("An enabled policy must include one-night or two-night gaps.");
    }
    if (
      item?.is_enabled !== true &&
      (item?.gap_1_enabled === true || item?.gap_2_enabled === true)
    ) {
      errors.push("A disabled policy cannot retain enabled gap lengths.");
    }
  });

  return [...new Set(errors)];
}

export function buildOrphanGapPolicyPayload(draft) {
  return {
    expected_configuration_hash: draft?.expected_configuration_hash || null,
    items: (Array.isArray(draft?.items) ? draft.items : []).map((item) => ({
      strategy_setting_id: Number(item.strategy_setting_id),
      is_enabled: item.is_enabled === true,
      gap_1_enabled: item.gap_1_enabled === true,
      gap_2_enabled: item.gap_2_enabled === true,
      far_horizon_days: Number(item.far_horizon_days),
    })),
  };
}
